import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ArrowRight, ChevronDown, ChevronUp, Loader2,
  Dog, Snowflake, MapPin, Fish, Footprints,
  Tent, Caravan, Sparkles, Users, Baby, Flame,
  PoundSterling, Star, CalendarCheck,
} from "lucide-react";

/**
 * HomeSearchWidget — campsite search/compare widget for the landing page hero.
 * Iter127: every chip is an additive toggle, not an immediate navigation.
 *
 *   Flow: user ticks any combination of chips + types a postcode/region →
 *   hits Search → we navigate to /browse with every selected filter applied,
 *   so the result is a *comparison* set of campsites around the surrounding
 *   area, sorted by distance when geolocation is available.
 *
 * "Near me" detail: ticking the chip flips a flag locally; on Search we ask
 * the browser for geolocation and pass `lat` + `lng` + `radius` URL params
 * to Browse. Browse picks those up as a fromPoint and applies the radius
 * filter. If the user blocks the geolocation prompt we still navigate, just
 * without the coords.
 */
export default function HomeSearchWidget() {
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("");
  const [pitch, setPitch] = useState("any");
  const [maxPrice, setMaxPrice] = useState(0);
  const [minRating, setMinRating] = useState(0);
  const [radiusKm, setRadiusKm] = useState(25);
  const [expanded, setExpanded] = useState(false);
  const [locating, setLocating] = useState(false);

  // Every selectable filter as additive toggles
  const [toggles, setToggles] = useState({
    dog_policy: false,
    open_year_round: false,
    has_fishery: false,
    has_walks: false,
    adults_only: false,
    family_friendly: false,
    group_friendly: false,
    bookable_now: false,
    direct_book: false,
    campfires: false,
    near_me: false,
    tent_only: false,
    caravan_friendly: false,
  });

  const nav = useNavigate();
  const toggle = (k) => setToggles((s) => ({ ...s, [k]: !s[k] }));

  const _buildParams = (coords) => {
    const sp = new URLSearchParams();
    if (q.trim()) sp.set("q", q.trim());
    if (region) sp.set("region", region);
    if (pitch && pitch !== "any") sp.set("pitch", pitch);
    if (maxPrice > 0) sp.set("max_price", String(maxPrice));
    if (minRating > 0) sp.set("min_rating", String(minRating));
    if (toggles.dog_policy) sp.set("dog_policy", "welcome");
    if (toggles.open_year_round) sp.set("open_year_round", "1");
    if (toggles.has_fishery) sp.set("has_fishery", "1");
    if (toggles.has_walks) sp.set("has_walks", "1");
    if (toggles.adults_only) sp.set("adults_only", "1");
    if (toggles.family_friendly) sp.set("family_friendly", "1");
    if (toggles.group_friendly) sp.set("group_friendly", "1");
    if (toggles.bookable_now) sp.set("bookable_now", "1");
    if (toggles.direct_book) sp.set("direct_book", "1");
    if (toggles.campfires) sp.set("campfires", "firepits");
    if (toggles.tent_only) sp.set("pitch", "tent");
    if (toggles.caravan_friendly && !toggles.tent_only) sp.set("pitch", "caravan");
    if (toggles.near_me) {
      sp.set("radius", String(radiusKm));
      if (coords) {
        sp.set("lat", coords.lat.toFixed(5));
        sp.set("lng", coords.lng.toFixed(5));
        sp.set("sort", "distance");
      }
    }
    return sp;
  };

  const _navigate = (sp) => nav(`/browse${sp.toString() ? `?${sp.toString()}` : ""}`);

  const onSubmit = (e) => {
    if (e) e.preventDefault();
    // If "Near me" is active and the browser supports geolocation, fetch
    // the user's coords first so /browse can sort campsites by distance.
    if (toggles.near_me && typeof navigator !== "undefined" && navigator.geolocation) {
      setLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocating(false);
          _navigate(_buildParams({ lat: pos.coords.latitude, lng: pos.coords.longitude }));
        },
        () => {
          // Permission denied / timeout — fall through without coords.
          setLocating(false);
          _navigate(_buildParams(null));
        },
        { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 6000 },
      );
      return;
    }
    _navigate(_buildParams(null));
  };

  // Count selected filters for the chip on More-filters / Search button hint
  const _selectedCount =
    Object.values(toggles).filter(Boolean).length +
    (region ? 1 : 0) +
    (pitch !== "any" ? 1 : 0) +
    (maxPrice > 0 ? 1 : 0) +
    (minRating > 0 ? 1 : 0);

  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-xl bg-white/95 backdrop-blur rounded-2xl shadow-[0_10px_30px_-8px_rgba(0,0,0,0.35)] border border-white/40 p-3 sm:p-4 mb-5"
      data-testid="home-search-widget"
    >
      {/* Idiot-proof header — keeps the flow obvious for first-time visitors */}
      <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-[#f0e8da]">
        <div className="mono text-[10px] sm:text-[11px] uppercase tracking-wider text-[#666] leading-tight">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#ff4500] text-white text-[9px] font-bold mr-1.5">1</span>
          Pick what matters
          <span className="mx-1.5 text-[#bbb]">·</span>
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#ff4500] text-white text-[9px] font-bold mr-1.5">2</span>
          Hit Search
          <span className="mx-1.5 text-[#bbb]">·</span>
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#ff4500] text-white text-[9px] font-bold mr-1.5">3</span>
          Compare
        </div>
        {_selectedCount > 0 && (
          <span className="mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#fff4ee] text-[#ff4500] font-bold border border-[#ffd9c2]" data-testid="home-search-count">
            {_selectedCount} picked
          </span>
        )}
      </div>
      {/* Row 1 — free-text + submit */}
      <label htmlFor="home-search-input" className="sr-only">Search campsites</label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" strokeWidth={2.2} />
          <input
            id="home-search-input"
            type="search"
            inputMode="search"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Postcode, region or campsite name…"
            className="w-full pl-9 pr-3 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-[#e5e5e5] rounded-xl focus:outline-none focus:border-[#ff4500] focus:ring-2 focus:ring-[#ff4500]/15 text-[#0e0e0e]"
            data-testid="home-search-input"
          />
        </div>
        <button
          type="submit"
          disabled={locating}
          className="inline-flex items-center gap-1.5 px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-60 text-white text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors shadow-sm shrink-0"
          data-testid="home-search-submit"
        >
          {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Search <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.4} /></>}
        </button>
      </div>

      {/* Row 2 — region + pitch type selectors */}
      <div className="grid grid-cols-2 gap-2 mt-2.5">
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="px-3 py-2 text-[12px] sm:text-sm bg-white border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500] focus:ring-2 focus:ring-[#ff4500]/15 text-[#0e0e0e]"
          data-testid="home-search-region"
        >
          <option value="">Any region</option>
          <optgroup label="England">
            <option>Cornwall, South West</option>
            <option>Devon, South West</option>
            <option>Dorset, South West</option>
            <option>Somerset, South West</option>
            <option>Gloucestershire, South West</option>
            <option>Cumbria, North West</option>
            <option>Lancashire, North West</option>
            <option>North Yorkshire, North East</option>
            <option>Lincolnshire, Central</option>
            <option>Norfolk, East Anglia</option>
            <option>Suffolk, East Anglia</option>
            <option>Hampshire, South East</option>
            <option>Kent, South East</option>
            <option>Isle of Wight, South East</option>
            <option>Sussex, South East</option>
          </optgroup>
          <optgroup label="Wales">
            <option>Gwynedd, North Wales</option>
            <option>Pembrokeshire, West Wales</option>
            <option>Conwy, North Wales</option>
            <option>Powys, Mid Wales</option>
          </optgroup>
          <optgroup label="Scotland">
            <option>Highlands, Scotland</option>
            <option>Argyll, Scotland</option>
            <option>Dumfries &amp; Galloway, Scotland</option>
          </optgroup>
          <optgroup label="Northern Ireland"><option>Northern Ireland</option></optgroup>
        </select>
        <select
          value={pitch}
          onChange={(e) => setPitch(e.target.value)}
          className="px-3 py-2 text-[12px] sm:text-sm bg-white border border-[#e5e5e5] rounded-lg focus:outline-none focus:border-[#ff4500] focus:ring-2 focus:ring-[#ff4500]/15 text-[#0e0e0e]"
          data-testid="home-search-pitch"
        >
          <option value="any">Any pitch type</option>
          <option value="tent">Tent</option>
          <option value="caravan">Caravan</option>
          <option value="motorhome">Motorhome</option>
          <option value="glamping">Glamping</option>
          <option value="campervan">Campervan</option>
        </select>
      </div>

      {/* Row 3 — most-asked quick chips. ALL are toggles — selected chips
          stay highlighted and feed into Search. */}
      <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="home-search-quickfilters">
        <QuickChip active={toggles.dog_policy} onClick={() => toggle("dog_policy")} icon={Dog} testid="quick-dogs">Dogs</QuickChip>
        <QuickChip active={toggles.open_year_round} onClick={() => toggle("open_year_round")} icon={Snowflake} testid="quick-year-round">Year-round</QuickChip>
        <QuickChip active={toggles.has_fishery} onClick={() => toggle("has_fishery")} icon={Fish} testid="quick-fishery">Fishery</QuickChip>
        <QuickChip active={toggles.near_me} onClick={() => toggle("near_me")} icon={MapPin} testid="quick-near-me">Near me</QuickChip>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-[#0e0e0e] text-[#0e0e0e] mono text-[10px] sm:text-[11px] uppercase tracking-wider font-bold hover:bg-[#0e0e0e] hover:text-white transition-colors ml-auto"
          data-testid="home-search-toggle-more"
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Less" : `More${_selectedCount > 0 ? ` · ${_selectedCount}` : ""}`}
        </button>
      </div>

      {/* Expanded panel — long-tail filters, all additive toggles */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#eee] space-y-3" data-testid="home-search-expanded">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mono text-[10px] uppercase tracking-wider text-[#666] flex items-center gap-1 mb-1">
                <PoundSterling className="w-3 h-3" /> Max £/night: <span className="text-[#ff4500] font-bold">{maxPrice === 0 ? "Any" : `£${maxPrice}`}</span>
              </label>
              <input type="range" min="0" max="100" step="5" value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="w-full accent-[#ff4500]" data-testid="home-search-maxprice" />
            </div>
            <div>
              <label className="mono text-[10px] uppercase tracking-wider text-[#666] flex items-center gap-1 mb-1">
                <Star className="w-3 h-3" /> Min rating: <span className="text-[#ff4500] font-bold">{minRating === 0 ? "Any" : `${minRating}★`}</span>
              </label>
              <input type="range" min="0" max="5" step="0.5" value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
                className="w-full accent-[#ff4500]" data-testid="home-search-minrating" />
            </div>
          </div>

          {/* Radius slider only visible when Near me is active */}
          {toggles.near_me && (
            <div>
              <label className="mono text-[10px] uppercase tracking-wider text-[#666] flex items-center gap-1 mb-1">
                <MapPin className="w-3 h-3" /> Within: <span className="text-[#ff4500] font-bold">{radiusKm} km of me</span>
              </label>
              <input type="range" min="5" max="100" step="5" value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="w-full accent-[#ff4500]" data-testid="home-search-radius" />
            </div>
          )}

          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1.5">Who&apos;s it for</div>
            <div className="flex flex-wrap gap-1.5">
              <QuickChip active={toggles.family_friendly} onClick={() => toggle("family_friendly")} icon={Baby} testid="quick-family">Family-friendly</QuickChip>
              <QuickChip active={toggles.adults_only} onClick={() => toggle("adults_only")} icon={Users} testid="quick-adults">Adults only</QuickChip>
              <QuickChip active={toggles.group_friendly} onClick={() => toggle("group_friendly")} icon={Users} testid="quick-groups">Groups</QuickChip>
            </div>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1.5">On site &amp; booking</div>
            <div className="flex flex-wrap gap-1.5">
              <QuickChip active={toggles.bookable_now} onClick={() => toggle("bookable_now")} icon={CalendarCheck} testid="quick-bookable">Bookable now</QuickChip>
              <QuickChip active={toggles.direct_book} onClick={() => toggle("direct_book")} icon={Sparkles} testid="quick-direct-book">Direct book</QuickChip>
              <QuickChip active={toggles.campfires} onClick={() => toggle("campfires")} icon={Flame} testid="quick-campfires">Campfires</QuickChip>
            </div>
          </div>

          <div>
            <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1.5">Activities &amp; pitch</div>
            <div className="flex flex-wrap gap-1.5">
              <QuickChip active={toggles.tent_only} onClick={() => toggle("tent_only")} icon={Tent} testid="quick-tent">Tent pitches</QuickChip>
              <QuickChip active={toggles.caravan_friendly} onClick={() => toggle("caravan_friendly")} icon={Caravan} testid="quick-caravan">Caravan pitches</QuickChip>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

function QuickChip({ children, onClick, icon: Icon, testid, active = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1 px-2.5 py-1 rounded-full mono text-[10px] sm:text-[11px] uppercase tracking-wider font-bold transition-colors " +
        (active
          ? "bg-[#ff4500] text-white border border-[#ff4500] shadow-sm"
          : "bg-[#fff4ee] border border-[#ffd9c2] text-[#ff4500] hover:bg-[#ff4500] hover:text-white")
      }
      data-testid={testid}
      aria-pressed={active}
    >
      {Icon ? <Icon className="w-3 h-3" strokeWidth={2.2} /> : null}
      <span>{children}</span>
    </button>
  );
}
